import React, { useEffect } from 'react';

const DependencyInversionPrinciple = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Dependency Inversion Principle (DIP) - SOLID Principles Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Dependency Inversion Principle - high-level modules should not depend on low-level modules. Learn dependency injection and inversion of control.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Dependency Inversion Principle - high-level modules should not depend on low-level modules. Learn dependency injection and inversion of control.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'dependency inversion principle, DIP, SOLID principles, dependency injection, inversion of control, software architecture');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'dependency inversion principle, DIP, SOLID principles, dependency injection, inversion of control, software architecture';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Dependency Inversion Principle (DIP)</h1>
        <p style={styles.subtitle}>
          The Fifth SOLID Principle - Depend on abstractions, not concretions
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Definition</h2>
          <blockquote style={styles.blockquote}>
            "High-level modules should not depend on low-level modules. Both should depend on abstractions."
          </blockquote>
          <p style={styles.paragraph}>
            The Dependency Inversion Principle states that high-level modules should not depend on low-level modules. 
            Both should depend on abstractions (interfaces or abstract classes). Additionally, abstractions should 
            not depend on details; details should depend on abstractions.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Key Concepts</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Inversion of Control:</strong> Control flow is inverted from the traditional approach</li>
            <li style={styles.listItem}><strong>Dependency Injection:</strong> Dependencies are provided from external sources</li>
            <li style={styles.listItem}><strong>Abstractions:</strong> Depend on interfaces, not concrete implementations</li>
            <li style={styles.listItem}><strong>Decoupling:</strong> Reduce coupling between high-level and low-level modules</li>
            <li style={styles.listItem}><strong>Flexibility:</strong> Easy to change implementations without affecting clients</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Violation Example</h2>
          <div style={styles.codeBlock}>
{`// BAD: Violates DIP - High-level depends on low-level
class MySQLDatabase {
    public void save(String data) {
        System.out.println("Saving to MySQL: " + data);
    }
    
    public String read(String id) {
        return "Data from MySQL: " + id;
    }
}

class EmailService {
    public void sendEmail(String message, String recipient) {
        System.out.println("Sending email to " + recipient + ": " + message);
    }
}

// High-level module depends directly on low-level modules
class OrderService {
    private MySQLDatabase database;     // Concrete dependency
    private EmailService emailService; // Concrete dependency
    
    public OrderService() {
        this.database = new MySQLDatabase();     // Tight coupling
        this.emailService = new EmailService(); // Tight coupling
    }
    
    public void processOrder(String orderData) {
        // Business logic
        database.save(orderData);
        emailService.sendEmail("Order processed", "customer@email.com");
    }
}

// Problems:
// 1. Hard to test (can't mock dependencies)
// 2. Hard to change database or email implementation
// 3. Violates Open/Closed Principle
// 4. High coupling between modules`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Correct Implementation</h2>
          <div style={styles.codeBlock}>
{`// GOOD: Following DIP - Depend on abstractions
interface Database {
    void save(String data);
    String read(String id);
}

interface NotificationService {
    void sendNotification(String message, String recipient);
}

// Low-level modules implement abstractions
class MySQLDatabase implements Database {
    @Override
    public void save(String data) {
        System.out.println("Saving to MySQL: " + data);
    }
    
    @Override
    public String read(String id) {
        return "Data from MySQL: " + id;
    }
}

class PostgreSQLDatabase implements Database {
    @Override
    public void save(String data) {
        System.out.println("Saving to PostgreSQL: " + data);
    }
    
    @Override
    public String read(String id) {
        return "Data from PostgreSQL: " + id;
    }
}

class EmailService implements NotificationService {
    @Override
    public void sendNotification(String message, String recipient) {
        System.out.println("Sending email to " + recipient + ": " + message);
    }
}

class SMSService implements NotificationService {
    @Override
    public void sendNotification(String message, String recipient) {
        System.out.println("Sending SMS to " + recipient + ": " + message);
    }
}

// High-level module depends on abstractions
class OrderService {
    private Database database;
    private NotificationService notificationService;
    
    // Dependency injection through constructor
    public OrderService(Database database, NotificationService notificationService) {
        this.database = database;
        this.notificationService = notificationService;
    }
    
    public void processOrder(String orderData) {
        // Business logic remains the same
        database.save(orderData);
        notificationService.sendNotification("Order processed", "customer@email.com");
    }
}

// Usage - dependencies injected from outside
public class DIPDemo {
    public static void main(String[] args) {
        // Can easily switch implementations
        Database database = new MySQLDatabase();
        NotificationService notificationService = new EmailService();
        
        OrderService orderService = new OrderService(database, notificationService);
        orderService.processOrder("Order #123");
        
        // Easy to change to different implementations
        Database postgresDB = new PostgreSQLDatabase();
        NotificationService smsService = new SMSService();
        
        OrderService orderService2 = new OrderService(postgresDB, smsService);
        orderService2.processOrder("Order #124");
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Benefits of Following DIP</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Flexibility:</strong> Easy to change implementations without affecting clients</li>
            <li style={styles.listItem}><strong>Testability:</strong> Easy to mock dependencies for unit testing</li>
            <li style={styles.listItem}><strong>Maintainability:</strong> Changes to low-level modules don't affect high-level modules</li>
            <li style={styles.listItem}><strong>Reusability:</strong> High-level modules can work with different implementations</li>
            <li style={styles.listItem}><strong>Decoupling:</strong> Reduced coupling between modules</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Dependency Injection Techniques</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Constructor Injection:</strong> Dependencies passed through constructor</li>
            <li style={styles.listItem}><strong>Setter Injection:</strong> Dependencies set through setter methods</li>
            <li style={styles.listItem}><strong>Interface Injection:</strong> Dependencies injected through interface methods</li>
            <li style={styles.listItem}><strong>Service Locator:</strong> Dependencies retrieved from a service locator</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Dependency Inversion Principle is crucial for creating flexible, maintainable, and testable software. 
            By depending on abstractions rather than concrete implementations, you create systems that are easy to 
            modify, extend, and test.
          </p>
          <p style={styles.paragraph}>
            DIP is often implemented using dependency injection frameworks, but the principle itself is about 
            designing your code to depend on stable abstractions rather than volatile concrete classes. 
            This leads to more robust and adaptable software architectures.
          </p>
        </section>
      </main>
    </article>
  );
};

export default DependencyInversionPrinciple;